import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box, Card, Grid, Typography, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, InputAdornment
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ReceiptIcon from "@material-ui/icons/Receipt";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format, subDays, isAfter } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  PieChart, Pie, Cell, Tooltip as ReTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, height: "100%", overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    transition: "transform 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: { padding: "20px 20px 16px", height: "100%" },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 32, fontWeight: 800, lineHeight: 1 },
  metricSub: { fontSize: 12, marginTop: 6, opacity: 0.85 },
  sectionLabel: { fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12, marginTop: 24 },
  chartCard: { borderRadius: 14, backgroundColor: "#fff", padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  tableCard: { borderRadius: 14, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableHead: { backgroundColor: theme.palette.primary.main },
  tableHeadCell: { color: "#fff !important", fontWeight: "700 !important", fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.5px" },
  tableRow: {
    "&:hover": { backgroundColor: "#f0fff4" },
    "&:nth-of-type(even)": { backgroundColor: "#fafafa" }
  },
}));

const PERIOD_OPTIONS = [
  { label: "Últimos 7 dias", value: 7 },
  { label: "Últimos 30 dias", value: 30 },
  { label: "Últimos 60 dias", value: 60 },
  { label: "Últimos 90 dias", value: 90 },
  { label: "Personalizado", value: "custom" },
];

const STATUS_OPTIONS = [
  { label: "Todos os status", value: "" },
  { label: "Pagas", value: "pago" },
  { label: "Pendentes", value: "pendente" },
  { label: "Vencidas", value: "vencido" },
  { label: "Canceladas", value: "cancelado" },
];

const STATUS_CONFIG = {
  pago:      { label: "Pago",      bg: "#f0fff4", color: "#276749", barColor: "#38a169" },
  pendente:  { label: "Pendente",  bg: "#fffaf0", color: "#c05621", barColor: "#ed8936" },
  vencido:   { label: "Vencido",   bg: "#fff5f5", color: "#c53030", barColor: "#e53e3e" },
  cancelado: { label: "Cancelado", bg: "#f7fafc", color: "#718096", barColor: "#a0aec0" },
};

const PIE_COLORS = { pago: "#38a169", pendente: "#ed8936", vencido: "#e53e3e", cancelado: "#a0aec0" };

const fmt = (d) => format(new Date(d), "yyyy-MM-dd");
const fmtDisplay = (d) => format(new Date(d + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR });
const fmtCurrency = (val) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(val || 0);

export default function RelatorioFaturas() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState(30);
  const [customStart, setCustomStart] = useState(fmt(subDays(new Date(), 30)));
  const [customEnd, setCustomEnd] = useState(fmt(new Date()));
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [faturas, setFaturas] = useState([]);
  const [sortBy, setSortBy] = useState("dataVencimento");
  const [sortDir, setSortDir] = useState("desc");

  const getDateRange = () => {
    if (period === "custom") return { start: customStart, end: customEnd };
    return { start: fmt(subDays(new Date(), period)), end: fmt(new Date()) };
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = { dateStart: start, dateEnd: end, pageSize: 500 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/faturas", { params });
      setFaturas(Array.isArray(data) ? data : data?.faturas || data?.data || []);
    } catch {
      setFaturas([]);
    }
    setLoading(false);
  }, [period, customStart, customEnd, statusFilter]);

  useEffect(() => { if (period !== "custom") loadData(); }, [period, statusFilter]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const getStatus = (fatura) => {
    if (fatura.status) return fatura.status.toLowerCase();
    if (fatura.dataPagamento) return "pago";
    const venc = new Date(fatura.dataVencimento);
    if (isAfter(new Date(), venc)) return "vencido";
    return "pendente";
  };

  const withStatus = faturas.map(f => ({ ...f, _status: getStatus(f) }));

  const filtered = withStatus
    .filter(f => {
      if (statusFilter && f._status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const nome = (f.contact?.name || f.descricao || "").toLowerCase();
        return nome.includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      let va = a[sortBy] ?? 0, vb = b[sortBy] ?? 0;
      if (sortBy === "valor") { va = Number(va); vb = Number(vb); }
      else if (typeof va === "string") { va = va.toLowerCase(); vb = String(vb).toLowerCase(); }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const totalFaturas = filtered.length;
  const totalPago = withStatus.filter(f => f._status === "pago").reduce((s, f) => s + Number(f.valor || 0), 0);
  const totalPendente = withStatus.filter(f => f._status === "pendente").reduce((s, f) => s + Number(f.valor || 0), 0);
  const totalVencido = withStatus.filter(f => f._status === "vencido").reduce((s, f) => s + Number(f.valor || 0), 0);
  const totalGeral = withStatus.reduce((s, f) => s + Number(f.valor || 0), 0);

  const statusCounts = {
    pago: withStatus.filter(f => f._status === "pago").length,
    pendente: withStatus.filter(f => f._status === "pendente").length,
    vencido: withStatus.filter(f => f._status === "vencido").length,
    cancelado: withStatus.filter(f => f._status === "cancelado").length,
  };

  const pieData = Object.entries(statusCounts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: STATUS_CONFIG[k]?.label || k, value: v, color: PIE_COLORS[k] }));

  const barData = [
    { name: "Pago", value: totalPago, color: "#38a169" },
    { name: "Pendente", value: totalPendente, color: "#ed8936" },
    { name: "Vencido", value: totalVencido, color: "#e53e3e" },
  ].filter(d => d.value > 0);

  const getPeriodLabel = () => {
    if (period === "custom") return `${fmtDisplay(customStart)} a ${fmtDisplay(customEnd)}`;
    return PERIOD_OPTIONS.find(o => o.value === period)?.label || "";
  };

  const handleExport = () => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const rows = filtered.map((f, i) => {
      const sc = STATUS_CONFIG[f._status] || STATUS_CONFIG.pendente;
      return `<tr><td>${i + 1}</td><td>${f.contact?.name || f.descricao || "—"}</td><td>${fmtDisplay((f.dataVencimento || "").slice(0, 10))}</td><td>${fmtCurrency(f.valor)}</td><td><span style="padding:2px 10px;border-radius:12px;background:${sc.bg};color:${sc.color};font-weight:600">${sc.label}</span></td></tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/><title>Relatório Faturas</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;background:#f5f5f5}
h1{font-size:26px;font-weight:800;margin-bottom:4px}p{color:#718096;margin-bottom:24px}
table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07)}
th{background:#38a169;color:#fff;padding:12px 16px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
td{padding:12px 16px;border-bottom:1px solid #f0f0f0;font-size:14px}tr:last-child td{border:none}
.footer{margin-top:32px;color:#a0aec0;font-size:12px}
.summary{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
.s-card{border-radius:10px;padding:14px;border:1px solid #e2e8f0}
.s-label{font-size:11px;text-transform:uppercase;letter-spacing:.5px;font-weight:700;margin-bottom:4px}
.s-val{font-size:20px;font-weight:800}</style></head>
<body><h1>💰 Relatório de Faturas</h1><p>Período: ${getPeriodLabel()} · Exportado em ${now}</p>
<div class="summary">
  <div class="s-card" style="background:#f0fff4"><div class="s-label" style="color:#276749">Recebido</div><div class="s-val" style="color:#276749">${fmtCurrency(totalPago)}</div></div>
  <div class="s-card" style="background:#fffaf0"><div class="s-label" style="color:#c05621">A Receber</div><div class="s-val" style="color:#c05621">${fmtCurrency(totalPendente)}</div></div>
  <div class="s-card" style="background:#fff5f5"><div class="s-label" style="color:#c53030">Vencido</div><div class="s-val" style="color:#c53030">${fmtCurrency(totalVencido)}</div></div>
  <div class="s-card" style="background:#f7fafc"><div class="s-label" style="color:#718096">Total</div><div class="s-val" style="color:#718096">${fmtCurrency(totalGeral)}</div></div>
</div>
<table><thead><tr><th>#</th><th>Cliente / Descrição</th><th>Vencimento</th><th>Valor</th><th>Status</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">${totalFaturas} faturas exibidas</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio-faturas-${fmt(new Date())}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Relatório de Faturas</Typography>
          <Typography className={classes.subtitle}>Receitas, cobranças e inadimplência por período</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExport}
            disabled={loading || !filtered.length}
            style={{ backgroundColor: "#38a169", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterCard}>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 140px" }}>
          <InputLabel>Período</InputLabel>
          <Select value={period} onChange={e => setPeriod(e.target.value)} label="Período">
            {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" style={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
            {STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        {period === "custom" && (
          <>
            <TextField label="De" type="date" size="small" variant="outlined"
              value={customStart} onChange={e => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ width: 160 }} />
            <TextField label="Até" type="date" size="small" variant="outlined"
              value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ width: 160 }} />
            <Button variant="contained" size="small" onClick={loadData}
              style={{ backgroundColor: "#38a169", color: "#fff", textTransform: "none", borderRadius: 8 }}>
              Aplicar
            </Button>
          </>
        )}
        <TextField placeholder="Buscar cliente..." size="small" variant="outlined"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#a0aec0" }} /></InputAdornment> }}
          style={{ width: 180 }} />
        {loading && <CircularProgress size={20} />}
        {!loading && faturas.length > 0 && (
          <Chip label={`${totalFaturas} faturas · ${getPeriodLabel()}`} size="small"
            style={{ backgroundColor: "#f0fff4", color: "#276749", fontWeight: 600 }} />
        )}
      </Box>

      <Typography className={classes.sectionLabel}>Resumo Financeiro</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#f0fff4,#c6f6d5)", borderTop: "3px solid #38a169" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#276749" }}>Recebido</Typography>
                <AttachMoneyIcon style={{ color: "#38a169", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#276749", fontSize: 22 }}>{loading ? "—" : fmtCurrency(totalPago)}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#276749" }}>{statusCounts.pago} fatura{statusCounts.pago !== 1 ? "s" : ""} pagas</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#fff3e5,#fbd38d)", borderTop: "3px solid #ed8936" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#c05621" }}>A Receber</Typography>
                <HourglassEmptyIcon style={{ color: "#ed8936", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#c05621", fontSize: 22 }}>{loading ? "—" : fmtCurrency(totalPendente)}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#c05621" }}>{statusCounts.pendente} fatura{statusCounts.pendente !== 1 ? "s" : ""} pendentes</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#fff5f5,#fed7d7)", borderTop: "3px solid #e53e3e" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#c53030" }}>Vencido</Typography>
                <TrendingDownIcon style={{ color: "#e53e3e", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#c53030", fontSize: 22 }}>{loading ? "—" : fmtCurrency(totalVencido)}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#c53030" }}>{statusCounts.vencido} fatura{statusCounts.vencido !== 1 ? "s" : ""} vencidas</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#ebf8ff,#bee3f8)", borderTop: "3px solid #4299e1" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#2b6cb0" }}>Total Faturado</Typography>
                <ReceiptIcon style={{ color: "#4299e1", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#2b6cb0", fontSize: 22 }}>{loading ? "—" : fmtCurrency(totalGeral)}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#2b6cb0" }}>{faturas.length} fatura{faturas.length !== 1 ? "s" : ""} no período</Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Distribuição</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Faturas por Status</Typography>
            {loading || pieData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <ReTooltip formatter={(val, name) => [`${val} faturas`, name]} />
                  <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Valor por Status</Typography>
            {loading || barData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={220}>
                {loading ? <CircularProgress /> : <Typography style={{ color: "#a0aec0" }}>Sem dados</Typography>}
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} margin={{ top: 20, right: 10, left: 10, bottom: 5 }} barSize={48}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={v => `R$ ${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
                  <ReTooltip formatter={(val) => [fmtCurrency(val), "Valor"]} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {barData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Faturas ({filtered.length})</Typography>
      <Card elevation={0} className={classes.tableCard}>
        <TableContainer>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell} style={{ width: 50 }}>#</TableCell>
                <TableCell className={classes.tableHeadCell}>
                  <TableSortLabel active={sortBy === "contact"} direction={sortBy === "contact" ? sortDir : "asc"}
                    onClick={() => handleSort("contact")} style={{ color: "#fff" }}>
                    Cliente / Descrição
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "dataVencimento"} direction={sortBy === "dataVencimento" ? sortDir : "desc"}
                    onClick={() => handleSort("dataVencimento")} style={{ color: "#fff" }}>
                    Vencimento
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="right">
                  <TableSortLabel active={sortBy === "valor"} direction={sortBy === "valor" ? sortDir : "desc"}
                    onClick={() => handleSort("valor")} style={{ color: "#fff" }}>
                    Valor
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" style={{ padding: 40 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" style={{ padding: 40, color: "#a0aec0" }}>
                    Nenhuma fatura encontrada para o filtro selecionado
                  </TableCell>
                </TableRow>
              ) : filtered.map((f, i) => {
                const sc = STATUS_CONFIG[f._status] || STATUS_CONFIG.pendente;
                const vencimento = f.dataVencimento
                  ? fmtDisplay((f.dataVencimento + "").slice(0, 10))
                  : "—";
                const clientName = f.contact?.name || f.descricao || `Fatura #${f.id}`;
                return (
                  <TableRow key={f.id} className={classes.tableRow}>
                    <TableCell style={{ color: "#a0aec0", fontSize: 12 }}>{i + 1}</TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, fontSize: 14 }}>{clientName}</Typography>
                      {f.descricao && f.contact?.name && (
                        <Typography style={{ fontSize: 11, color: "#a0aec0" }}>{f.descricao}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 13 }}>{vencimento}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography style={{ fontWeight: 700, fontSize: 14, color: f._status === "pago" ? "#276749" : f._status === "vencido" ? "#c53030" : "#2d3748" }}>
                        {fmtCurrency(f.valor)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={sc.label} size="small"
                        style={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 700, fontSize: 11, minWidth: 80 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
